import { ExpandoDirection, ExpandoWidget } from '@tableau/widgets';
import React from 'react';
import { style } from 'typestyle';
import {
  divWithStyle,
  elideTextStyle,
  Style,
  useTelemetrySender,
  ViewerPaneObfuscatedPlaceholder,
} from '../../../data-catalog-shared';
import { SvgFieldIcon } from '../field/components/SvgFieldIcon';
import { iconForField } from '../field/functions/FieldFunctions';
import { FieldNameMappings } from '../field/functions/FieldNameMapping';
import { FieldDescriptionViewedTelemetryEvent } from './FieldDescriptionViewedTelemetryEvent';
import { SheetField } from './SheetField';
import { SheetFieldListElementContainer } from './ViewerPaneStyles';
import { VizClientFieldCard } from './VizClientFieldCard';
import { useToggle } from './useExpand';

const FieldIconHeight = 17;

export interface VizClientFieldElementProps {
  readonly field: SheetField;
  readonly workbookVizportalUrlId?: string;
}

// this is a workaround since the shared widgets expando doesn't have a prop that allows us to right
// align the carat, note that this breaks the contract with shared widgets, so if a bug occurs as a
// result of the widget being updated, its on us to fix it
const expandoCaratRightAlignedStyle: Style = {
  $nest: {
    '& > [role="button"]': {
      width: '100%'
    },
    '& > [role="button"] > div': {
      flexGrow: 1
    },
    '& > [role="button"] > img': {
      order: 1
    },
  },
};

const FieldLabel = divWithStyle('VizClientFieldLabel', {
  ...elideTextStyle,
  maxWidth: 225 // TODO: try to remove hardcoded width (makes eliding work)
});

const FieldWrapper = divWithStyle('VizClientFieldWrapper', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
});

const FieldCardFieldIcon = divWithStyle('VizClientFieldCardFieldIcon', {
  flexShrink: 0,
  height: FieldIconHeight,
})

export function VizClientFieldElement(props: VizClientFieldElementProps): JSX.Element {
  const { state: isExpanded, toggle: toggleExpando } = useToggle();
  const sendDataInFieldDescriptionViewed = useTelemetrySender<FieldDescriptionViewedTelemetryEvent>({ type: 'field_description_viewed' });

  const launchExpando = React.useCallback(() => {
    toggleExpando(true);
    sendDataInFieldDescriptionViewed({
      source: 'vizclient',
    });
  }, [toggleExpando, sendDataInFieldDescriptionViewed])

  const isObfuscated: boolean = props.field.name === null;

  const renderIconWithFieldName = () => {
    const fieldIcon = iconForField(props.field);
    return (
      <FieldWrapper>
        <FieldCardFieldIcon title={fieldIcon ? FieldNameMappings[fieldIcon]() : ''}>
          <SvgFieldIcon fieldIcon={fieldIcon} />
        </FieldCardFieldIcon>
        <FieldLabel>{props.field.name}</FieldLabel>
      </FieldWrapper>
    );
  }

  return (
    <SheetFieldListElementContainer data-tb-test-id='data-details-field-element-container'>
      {isObfuscated ? ViewerPaneObfuscatedPlaceholder() :
        <ExpandoWidget
          className={style(expandoCaratRightAlignedStyle)}
          direction={ExpandoDirection.Down}
          isExpanded={isExpanded}
          onCollapse={() => toggleExpando(false)}
          onExpand={launchExpando}
          testId={'data-details-field-element-expando'}
          style={{ display: 'block' }} // so expando content fills width
          label={renderIconWithFieldName()}
        >
          {isExpanded ? (
            <VizClientFieldCard
              field={props.field}
              workbookVizportalUrlId={props.workbookVizportalUrlId}
            />
          ) : null}
        </ExpandoWidget>
      }
    </SheetFieldListElementContainer>
  );
}
